// Ad attribution for the web2app funnel's in-house checkout (the paywall +
// Stripe steps at the end of /funnel-auth). This is the
// browser half of what RevenueCat's hosted funnel did for us: hold on to the
// click that brought the parent here, hand it to the backend once they have an
// account, and fire the browser-side conversion pixels with ids the server's
// Conversions API events share, so each network counts the purchase once.
//
//  - captureAdClicks(): on landing, keep fbclid / gclid (+ gbraid / wbraid)
//    and the utm_* params from the URL. The funnel's Google OAuth round trip
//    comes back to a URL without them, so they live in localStorage, newest
//    click wins, for as long as each network can still attribute it.
//  - funnelAttribution(): everything the backend stores against the account
//    for the server-side events: TikTok (utils/tiktokPixel), Meta `_fbp`/`_fbc`
//    cookies (or `_fbc` rebuilt from fbclid the way Meta specifies), gclid,
//    UTMs and the landing page.
//  - metaTrack(): the Meta Pixel's funnel-step events (InitiateCheckout). The
//    purchase itself is reported to Meta by RevenueCat, never by the page.
//    The server sends the same event with the same event_id (see
//    Pixley-Backend/integrations/meta_capi.py), so Meta dedups the pair.
//
// The Meta Pixel itself is loaded site-wide from public/index.html (`fbq`);
// nothing here loads it. Everything is wrapped so a blocked pixel or a
// private-mode storage failure can never break the funnel.
import { logger } from './logger';
import { tiktokAttribution } from './tiktokPixel';

export const AD_CLICK_KEY = 'pixley_ad_click';
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// Meta attributes a click for 7 days, Google for up to 90. The stored click is
// kept for the longer window; the server applies each network's own rule.
const CLICK_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const TOKEN = /^[!-~]{1,512}$/;
const MAX_UTM_LEN = 200;

const readCookie = (name) => {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) {
    return null;
  }
};

const token = (value) => (typeof value === 'string' && TOKEN.test(value) ? value : null);

/**
 * Remember the ad click on the landing URL. A fresh click id in the URL always
 * replaces a stored one (a returning parent is matched to the ad they clicked
 * this time); UTMs are only taken together with the click they came with, or
 * on their own when there is no stored click yet.
 */
export function captureAdClicks() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fresh = {};
    for (const key of ['fbclid', 'gclid', 'gbraid', 'wbraid']) {
      const value = token(params.get(key));
      if (value) fresh[key] = value;
    }
    const utm = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value && value.trim()) utm[key] = value.trim().slice(0, MAX_UTM_LEN);
    }
    const hasClick = Object.keys(fresh).length > 0;
    const stored = readStoredAdClick();
    if (!hasClick && (!Object.keys(utm).length || stored)) return stored;

    const record = {
      ...fresh,
      utm: Object.keys(utm).length ? utm : stored?.utm || null,
      landing_page: `${window.location.origin}${window.location.pathname}`,
      at: Date.now(),
    };
    localStorage.setItem(AD_CLICK_KEY, JSON.stringify(record));
    return record;
  } catch (_) {
    return null;
  }
}

export function readStoredAdClick() {
  try {
    const raw = localStorage.getItem(AD_CLICK_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (!record || typeof record.at !== 'number' || Date.now() - record.at > CLICK_MAX_AGE_MS) {
      localStorage.removeItem(AD_CLICK_KEY);
      return null;
    }
    return record;
  } catch (_) {
    return null;
  }
}

/**
 * Meta's `_fbc` cookie, or one built from the fbclid the way the Pixel would
 * have (`fb.1.<click time ms>.<fbclid>`) when the Pixel hasn't written it —
 * blocked, or the landing happened before it loaded.
 */
export function metaClickId(stored = readStoredAdClick()) {
  const cookie = readCookie('_fbc');
  if (cookie && TOKEN.test(cookie)) return cookie;
  if (stored?.fbclid) return `fb.1.${stored.at}.${stored.fbclid}`;
  return null;
}

/** What the backend stores against the account for server-side conversions. */
export function funnelAttribution() {
  const out = { ...tiktokAttribution() };
  try {
    const stored = readStoredAdClick();
    const fbc = metaClickId(stored);
    const fbp = readCookie('_fbp');
    if (fbc) out.fbc = fbc;
    if (fbp && TOKEN.test(fbp)) out.fbp = fbp;
    if (stored) {
      for (const key of ['fbclid', 'gclid', 'gbraid', 'wbraid']) {
        if (stored[key]) out[key] = stored[key];
      }
      if (stored.utm) {
        for (const key of UTM_KEYS) {
          if (stored.utm[key]) out[key] = stored.utm[key];
        }
      }
      if (stored.landing_page) out.landing_page = stored.landing_page;
    }
  } catch (_) {
    // attribution is best effort
  }
  return out;
}

/**
 * The id the server uses for the same conversion (meta_capi.py): one per
 * account per conversion type, so a replayed success screen and the server's
 * copy all collapse into one event on Meta's side.
 */
export function metaTrack(event, params = {}, eventId) {
  try {
    if (typeof window.fbq !== 'function') return false;
    if (eventId) window.fbq('track', event, params, { eventID: eventId });
    else window.fbq('track', event, params);
    return true;
  } catch (e) {
    logger.warn('Meta pixel track failed (non-blocking):', e);
    return false;
  }
}

/** Dollar value of what was just bought, for the browser pixels' `value`. */
export const purchaseValue = ({ amountCents, fallback = 0 } = {}) =>
  Number.isFinite(amountCents) ? Math.round(amountCents) / 100 : fallback;
